import React from "react";
import PropTypes from "prop-types";
import { Table, Button, ModalFooter } from "reactstrap";
import { formatCurrency } from "common/number-formatter";

const propTypes = {
  cancelFlowData: PropTypes.shape({
    zip_spots_info: PropTypes.arrayOf(
      PropTypes.shape({
        postal_code: PropTypes.string.isRequired,
        spots: PropTypes.number.isRequired,
        paid_total: PropTypes.number.isRequired,
        new_paid_total: PropTypes.number.isRequired,
        spot_type: PropTypes.string.isRequired
      })
    ).isRequired,
    coupon_recurring_cents: PropTypes.number.isRequired,
    coupon_recurring_retention_cents: PropTypes.number.isRequired,
    total_cost_recurring: PropTypes.number.isRequired,
    total_cost_new: PropTypes.number.isRequired,
    total_coupon_savings: PropTypes.number.isRequired,
    coupon_savings_pct: PropTypes.number.isRequired
  }).isRequired,
  onContinue: PropTypes.func.isRequired,
  onKeep: PropTypes.func.isRequired
};

const CouponSavingsStep = ({ cancelFlowData, onContinue, onKeep }) => {
  const cfd = cancelFlowData || {};
  const zips = Object.values(cfd.zip_spots_info || {}).map(z => ({
    postal_code: z.postal_code,
    spots: z.spots,
    spot_type: z.spot_type,
    paid_total: z.paid_total / 100,
    new_paid_total: z.new_paid_total / 100
  }));

  const cents = v => (v || 0) / 100;

  const couponRecurringCents = cents(cfd.coupon_recurring_cents);
  const couponRecurringRetentionCents = cents(cfd.coupon_recurring_retention_cents);
  const totalCostRecurring = cents(cfd.total_cost_recurring);
  const totalCostNew = cents(cfd.total_cost_new);
  const totalCouponSavings = cents(cfd.total_coupon_savings);
  const couponSavingsPct = Math.round(cfd.coupon_savings_pct || 0);

  return (
    <div>
      <p className="text-center font-weight-bold mb-1">
        Before you go, did you know that you are saving{" "}
        <span className="text-success">{formatCurrency(totalCouponSavings)}</span> per period,
        forever?
      </p>

      <Table bordered hover size="sm" className="mb-2">
        <thead>
          <tr>
            <th>Zip Code</th>
            <th className="text-center"># of Spots</th>
            <th className="text-center">Your Rate</th>
            <th className="text-center">List Price</th>
          </tr>
        </thead>
        <tbody>
          {zips.map(z => (
            <tr key={`${z.postal_code}-${z.spot_type}`}>
              <td>{z.postal_code}</td>
              <td className="text-center">
                {z.spots}{" "}
                {z.spot_type
                  ? `${z.spot_type} spot${z.spots !== 1 ? "s" : ""}`
                  : `spot${z.spots !== 1 ? "s" : ""}`}
              </td>
              <td className="text-center text-success">{formatCurrency(z.paid_total)}</td>
              <td className="text-center">{formatCurrency(z.new_paid_total)}</td>
            </tr>
          ))}
          {couponRecurringCents > 0 && (
            <tr>
              <td colSpan={2} className="text-right text-muted">
                Coupons
              </td>
              <td className="text-center text-success">{formatCurrency(-couponRecurringCents)}</td>
              <td />
            </tr>
          )}
          {couponRecurringRetentionCents > 0 && (
            <tr>
              <td colSpan={2} className="text-right text-muted">
                Renewal Discount
              </td>
              <td className="text-center text-success">
                {formatCurrency(-couponRecurringRetentionCents)}
              </td>
              <td />
            </tr>
          )}
          <tr className="font-weight-bold">
            <td colSpan={2} className="text-right">
              Total
            </td>
            <td className="text-center text-success">{formatCurrency(totalCostRecurring)}</td>
            <td className="text-center">{formatCurrency(totalCostNew)}</td>
          </tr>
        </tbody>
      </Table>

      {couponSavingsPct > 0 && (
        <p className="text-center text-success mb-2">
          You&apos;re saving <strong>{couponSavingsPct}%</strong> over other agents!
        </p>
      )}

      <p className="text-center text-muted mb-0">
        If you cancel, you&apos;ll lose your lower rate and have to pay{" "}
        <strong>{formatCurrency(totalCouponSavings)} more</strong> per period.
      </p>

      <ModalFooter className="pt-2 px-0">
        <Button color="secondary" className="btn-sharp" onClick={onContinue}>
          Cancel and Give Up Lower Rate
        </Button>
        <Button color="primary" className="btn-sharp" onClick={onKeep}>
          Keep My Lower Rate
        </Button>
      </ModalFooter>
    </div>
  );
};

CouponSavingsStep.propTypes = propTypes;

export default CouponSavingsStep;
